import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import {
  getAssessmentFromTask,
  getAssessmentUrlWithUserId,
  isAssessmentReadyToStart,
} from 'src/careflow/helpers/common';
import useAuth from '../../authentication/hook/useAuth';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';

export const useTaskActivityDefinition = () => {
  const { data: taskADRegistry } = useQuery({
    queryKey: ['taskActivityDefinition'],
    queryFn: async () => {
      const taskActivityDefinition = await getRegistries(undefined, 'task-activity-definition');
      return taskActivityDefinition;
    },
  });

  const taskActivityDefinition = useMemo(
    () => (taskADRegistry?.[0]?.keyValue ? JSON.parse(taskADRegistry?.[0]?.keyValue) : null),
    [taskADRegistry]
  );

  return taskActivityDefinition;
};

export const useTaskAssessment = (tasks: any[], refreshTasks: () => Promise<any>) => {
  const user = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [openAssessmentFrame, setOpenAssessmentFrame] = useState(false);
  const [selectedTaskAssessmentId, setSelectedTaskAssessmentId] = useState<null | string>(null);
  const [selectedTaskAssessmentUrl, setSelectedTaskAssessmentUrl] = useState<null | string>(null);
  const [clickedTaskAssessment, setClickedTaskAssessment] = useState<Task | null>(null);

  const selectedTaskAssessment = useMemo(() => {
    if (!selectedTaskAssessmentId) return null;
    return tasks.find((t) => t?.id === selectedTaskAssessmentId) ?? clickedTaskAssessment;
  }, [tasks, selectedTaskAssessmentId, clickedTaskAssessment]);

  useEffect(() => {
    const openOrUpdateAssessment = () => {
      const inputAssessment = selectedTaskAssessment
        ? getAssessmentFromTask(selectedTaskAssessment)
        : null;

      if (!inputAssessment) {
        enqueueSnackbar('Could not open the assessment for this task.', {
          variant: 'error',
        });
      } else if (isAssessmentReadyToStart(inputAssessment)) {
        const authUser = user.getCurrentUser();
        const assessmentUrl = getAssessmentUrlWithUserId(
          inputAssessment,
          authUser?.user_fhir_uri
        );
        setSelectedTaskAssessmentUrl(assessmentUrl ?? null);
        setOpenAssessmentFrame(true);
      } else {
        enqueueSnackbar('Preparing assessment. Please try again later.', {
          variant: 'error',
        });
      }
      setSelectedTaskAssessmentId(null);
      setClickedTaskAssessment(null);
    };

    if (selectedTaskAssessmentId) {
      openOrUpdateAssessment();
    }
  }, [selectedTaskAssessmentId, tasks, selectedTaskAssessment, user, enqueueSnackbar]);

  const handleAssessmentSelect = async (task: Task) => {
    if (!task.id) return;

    const inputAssessment = getAssessmentFromTask(task);
    if (!inputAssessment) return;

    setClickedTaskAssessment(task);

    if (!isAssessmentReadyToStart(inputAssessment)) {
      await refreshTasks();
    }

    setSelectedTaskAssessmentId(task.id);
  };

  const onCancelAssessment = () => {
    setOpenAssessmentFrame(false);
    setSelectedTaskAssessmentId(null);
    setClickedTaskAssessment(null);
    setSelectedTaskAssessmentUrl(null);
  };

  return {
    openAssessmentFrame,
    selectedTaskAssessmentUrl,
    handleAssessmentSelect,
    onCancelAssessment,
    setOpenAssessmentFrame,
    setSelectedTaskAssessmentId,
    setSelectedTaskAssessmentUrl
  };
};
